const tolerance = 2;

export function collectionSliderGeometry(cards, viewport, rtl = false) {
  if (!cards.length) return { overflow: false, atStart: true, atEnd: true, previous: 0, next: 0, home: 0, end: 0 };
  const direction = rtl ? -1 : 1;
  const starts = cards.map(card => rtl ? viewport.right - card.right : card.left - viewport.left);
  const first = cards[0];
  const last = cards.at(-1);
  const span = rtl ? first.right - last.left : last.right - first.left;
  const forward = Math.max(0, rtl ? viewport.left - last.left : last.right - viewport.right);
  const backward = Math.max(0, -starts[0]);
  const overflow = span > viewport.right - viewport.left + tolerance;
  const atStart = !overflow || backward <= tolerance;
  const atEnd = !overflow || forward <= tolerance;
  const next = starts.find(value => value > tolerance) ?? forward;
  const previous = starts.filter(value => value < -tolerance).at(-1) ?? -backward;
  return {
    overflow,
    atStart,
    atEnd,
    previous: atStart ? 0 : direction * Math.max(previous, -backward),
    next: atEnd ? 0 : direction * Math.min(next, forward),
    home: atStart ? 0 : -direction * backward,
    end: atEnd ? 0 : direction * forward
  };
}

if (typeof customElements !== 'undefined' && !customElements.get('oyster-collection-slider')) {
  customElements.define('oyster-collection-slider', class extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.track = this.querySelector('[data-collection-track]');
      this.controls = this.querySelector('[data-collection-controls]');
      this.previous = this.querySelector('[data-collection-previous]');
      this.next = this.querySelector('[data-collection-next]');
      if (!this.track || !this.controls || !this.previous || !this.next) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.previous.disabled = false;
      this.next.disabled = false;
      this.previous.addEventListener('click', () => this.move('previous'), { signal });
      this.next.addEventListener('click', () => this.move('next'), { signal });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const action = { ArrowLeft: rtl ? 'next' : 'previous', ArrowRight: rtl ? 'previous' : 'next', Home: 'home', End: 'end' }[event.key];
        if (!action || !this.measure().state.overflow) return;
        event.preventDefault();
        this.move(action);
      }, { signal });
      this.track.addEventListener('focusin', event => {
        const card = event.target.closest?.('[data-collection-card]');
        if (card && this.track.contains(card)) this.reveal(card);
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest?.('[data-collection-card]')
          || this.cards().find(item => item.dataset.blockId === String(event.detail?.blockId));
        if (card && this.track.contains(card)) this.reveal(card);
      }, { signal });
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.scheduleUpdate());
        this.observer.observe(this.track);
        this.cards().forEach(card => this.observer.observe(card));
      } else {
        window.addEventListener('resize', () => this.scheduleUpdate(), { passive: true, signal });
      }
      this.update();
    }

    cards() {
      return Array.from(this.track.querySelectorAll('[data-collection-card]')).filter(card => card.getBoundingClientRect().width > 0);
    }

    measure() {
      const rect = this.track.getBoundingClientRect();
      const style = getComputedStyle(this.track);
      const left = rect.left + this.track.clientLeft;
      const viewport = {
        left: left + (parseFloat(style.scrollPaddingLeft) || 0),
        right: left + this.track.clientWidth - (parseFloat(style.scrollPaddingRight) || 0)
      };
      const rtl = style.direction === 'rtl';
      return { viewport, rtl, state: collectionSliderGeometry(this.cards().map(card => card.getBoundingClientRect()), viewport, rtl) };
    }

    scheduleUpdate() {
      if (this.frame != null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.abort?.signal.aborted) this.update();
      });
    }

    update() {
      const { state } = this.measure();
      this.controls.hidden = !state.overflow;
      this.previous.setAttribute('aria-disabled', String(state.atStart));
      this.next.setAttribute('aria-disabled', String(state.atEnd));
    }

    move(action) {
      const delta = this.measure().state[action];
      if (!Number.isFinite(delta) || Math.abs(delta) <= tolerance) return;
      this.track.scrollBy({ left: delta, behavior: this.motion.matches ? 'instant' : 'smooth' });
      this.scheduleUpdate();
    }

    reveal(card) {
      const { viewport, rtl } = this.measure();
      const rect = card.getBoundingClientRect();
      let delta = 0;
      if (rect.width > viewport.right - viewport.left) delta = rtl ? rect.right - viewport.right : rect.left - viewport.left;
      else if (rect.left < viewport.left) delta = rect.left - viewport.left;
      else if (rect.right > viewport.right) delta = rect.right - viewport.right;
      if (Math.abs(delta) > tolerance) this.track.scrollBy({ left: delta, behavior: 'instant' });
      this.scheduleUpdate();
    }

    disconnect() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    disconnectedCallback() {
      this.disconnect();
    }
  });
}
